/**
 * SpecHeader — top header strip for the spec page.
 *
 *   - Back button
 *   - Title + status summary subtitle (drafted / empty counts)
 *   - Live auto-save indicator (AutoSaveChip)
 *   - Action buttons: Generate Everything, Preview DOCX,
 *     Export menu (DOCX / PDF / Bundle ZIP).
 */
import React, { useState } from 'react';
import { ArrowLeft, Settings as SettingsIcon } from 'lucide-react';
import CoursesLink from '@/components/layout/CoursesLink';
import {
  type SpecDocument,
  type SpecSectionSummary,
} from '@/api/client';
import SpecExportMenu from './SpecExportMenu';
import { useSpecStore } from '@/store/specStore';
import BundleExportModal from './BundleExportModal';
import ConfirmDialog from './ConfirmDialog';
import CostIndicator from './CostIndicator';
import GenerateEverythingDialog from './GenerateEverythingDialog';
import GenerateEverythingHistory from './GenerateEverythingHistory';
import WordTargetsDialog from './WordTargetsDialog';

const outlineBtn =
  'inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-zinc-300 ' +
  'bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 ' +
  'transition-colors';

/** Phase 5 Slice 5E — auto-save status indicator.
 *
 * State machine driven by the spec store:
 *   read-only   when active section is frozen / auto-generated /
 *               none selected (no editable surface)
 *   saving…     when paragraphSaveStatus='saving' (the debounced
 *               PATCH is in flight)
 *   error       when paragraphSaveStatus='error' (PATCH failed —
 *               the chip surfaces this so the drafter sees the
 *               problem at a glance; full error message is in the
 *               toast)
 *   auto-saved · HH:MM   when lastSavedAt is set and not saving
 *   (empty)     before the first save lands
 */
function AutoSaveChip(): JSX.Element {
  const status = useSpecStore((s) => s.paragraphSaveStatus);
  const lastSavedAt = useSpecStore((s) => s.lastSavedAt);
  const sections = useSpecStore((s) => s.sections);
  const activeSectionId = useSpecStore((s) => s.activeSectionId);
  const offlineMode = useSpecStore((s) => s.offlineMode);
  const pendingOfflineCount = useSpecStore((s) => s.pendingOfflineCount);
  const active = activeSectionId
    ? sections.find((s) => s.id === activeSectionId) ?? null
    : null;
  const readOnly =
    !active || active.is_auto_generated || active.is_frozen;

  // Offline mode takes priority over read-only / saving / saved.
  // Brief §10: "Working offline · N changes queued".
  if (offlineMode) {
    const label =
      pendingOfflineCount > 0
        ? `working offline · ${pendingOfflineCount} queued`
        : 'working offline';
    return (
      <span
        className="inline-flex h-7 items-center rounded-sm bg-amber-50 px-2 font-mono text-[11px] text-amber-700"
        data-testid="spec-autosave-chip"
        data-state="offline"
        title="Auto-save is queued in the browser; will flush on reconnect"
      >
        {label}
      </span>
    );
  }

  let label: string;
  let cls = 'bg-zinc-100 text-zinc-500';
  let title = '';
  if (readOnly) {
    label = 'read-only';
    title = active?.is_frozen
      ? 'Section is frozen — unfreeze to edit'
      : active?.is_auto_generated
        ? 'Auto-generated section — content rendered live'
        : 'No active section';
  } else if (status === 'saving') {
    label = 'saving…';
    cls = 'bg-blue-50 text-blue-700';
    title = 'Saving paragraph edits';
  } else if (status === 'error') {
    label = 'save failed';
    cls = 'bg-amber-50 text-amber-700';
    title = 'Auto-save failed; see toast';
  } else if (lastSavedAt) {
    const t = new Date(lastSavedAt);
    const hh = t.getHours().toString().padStart(2, '0');
    const mm = t.getMinutes().toString().padStart(2, '0');
    label = `auto-saved · ${hh}:${mm}`;
    cls = 'bg-emerald-50 text-emerald-700';
  } else {
    label = 'auto-save';
    title = 'Auto-save active; no edits yet';
  }
  return (
    <span
      className={`inline-flex h-7 items-center rounded-sm px-2 font-mono text-[11px] ${cls}`}
      data-testid="spec-autosave-chip"
      data-state={readOnly ? 'read-only' : status === 'saving' ? 'saving' : status === 'error' ? 'error' : lastSavedAt ? 'idle-saved' : 'idle-fresh'}
      title={title}
    >
      {label}
    </span>
  );
}

interface Props {
  matterId: string;
  specDocument: SpecDocument | null;
  sectionSummary: SpecSectionSummary | null;
  frozenClaimsCount?: number;
  frozenDrawingsCount?: number;
  onBack?: () => void;
  onOpenSettings?: () => void;
  /** The user-guide videos (the Courses link). */
  onOpenCourses?: () => void;
  /** DD workbench — Dhruva's claim-by-claim Description page
   * (docs/DETAILED_DESCRIPTION_WORKBENCH_PLAN.md DD-4). */
  onOpenDescription?: () => void;
  /** BUG-036 — gate the CostIndicator on the caller-resolved
   * role rather than letting CostIndicator self-resolve via
   * /auth/me. The component already returned null for non-admin,
   * but a defence-in-depth gate at the mount point removes any
   * chance of a flicker before /auth/me resolves and avoids
   * leaking telemetry endpoint hits from drafter sessions. */
  userRole?: string | null;
}

export default function SpecHeader({
  matterId,
  specDocument,
  sectionSummary,
  frozenClaimsCount,
  frozenDrawingsCount,
  onBack,
  onOpenSettings,
  onOpenDescription,
  onOpenCourses,
  userRole,
}: Props): JSX.Element {
  const isAdmin = userRole === 'admin';
  // Phase 10 Slice 10E — Preview DOCX + Export bundle modal.
  // Both gate on `hasMinimumContent` (at least one drafted
  // section has paragraphs); the section summary surfaces this
  // as `drafted` count.
  const hasMinimumContent = (sectionSummary?.drafted ?? 0) > 0;
  // Same shape the app writes back for deep links (App.js), built from
  // the current location so a sub-path deploy still resolves.
  const descriptionHref = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}`
      + `${window.location.search}#/description/${matterId}`
    : `#/description/${matterId}`;
  const [bundleModalOpen, setBundleModalOpen] = useState(false);
  const [geDialogOpen, setGeDialogOpen] = useState(false);
  // Drafter-set length budget for this matter — overall, or section
  // by section. Distinct from the admin's per-jurisdiction defaults,
  // which is where a matter's starting numbers come from.
  const [wordTargetsOpen, setWordTargetsOpen] = useState(false);
  // Phase 11 Slice 11D — disable Generate Everything when a job
  // is already running for this matter.
  const geRunning = useSpecStore((s) => {
    const st = s.generateEverythingStatus;
    return !!st && (st.status === 'queued' || st.status === 'running');
  });
  // Bulk reset wires through the same store action ActionBar uses
  // for the per-section reset; eligible sections (non-frozen, non-
  // auto) get cleared in one DELETE on the backend.
  const resetAllSections = useSpecStore((s) => s.resetAllSections);
  const showToast = useSpecStore((s) => s.showToast);
  const [resettingAll, setResettingAll] = useState(false);
  // UX-F01 (Phase 27 3-F) — replaces window.confirm() with the
  // styled ConfirmDialog. confirmResetAllOpen is the open-flag;
  // the actual reset runs in handleResetAllConfirmed below when
  // the user clicks the destructive button.
  const [confirmResetAllOpen, setConfirmResetAllOpen] = useState(false);
  const handleResetAllClick = () => {
    if (!hasMinimumContent || resettingAll || geRunning) return;
    setConfirmResetAllOpen(true);
  };
  const handleResetAllConfirmed = async () => {
    setResettingAll(true);
    try {
      const outcome = await resetAllSections();
      if (outcome.kind === 'error') {
        showToast(`Reset all failed: ${outcome.message}`);
      } else {
        showToast(
          `Reset — cleared ${outcome.deleted} paragraph(s) across `
          + `${outcome.sections} section(s).`,
        );
      }
    } finally {
      setResettingAll(false);
      setConfirmResetAllOpen(false);
    }
  };
  // Total sections drafted; sectionSummary.total minus drafted +
  // auto count tells us how many empties remain. Cheap derivation.
  const hasEmptyDrafted = (sectionSummary?.total ?? 0)
    - (sectionSummary?.drafted ?? 0)
    - 2 // 2 auto sections (reference_signs_list + claims_mirror)
    > 0;
  const subtitle = (() => {
    if (!specDocument || !sectionSummary) return null;
    const parts: string[] = [];
    parts.push(`${specDocument.jurisdiction} draft`);
    parts.push(
      `${sectionSummary.total} section${sectionSummary.total === 1 ? '' : 's'}`,
    );
    if (sectionSummary.drafted > 0) {
      parts.push(`${sectionSummary.drafted} drafted`);
    }
    if (sectionSummary.with_issues > 0) {
      parts.push(`${sectionSummary.with_issues} with issues`);
    }
    if (sectionSummary.empty > 0) {
      parts.push(`${sectionSummary.empty} empty`);
    }
    const grounding: string[] = [];
    if (frozenClaimsCount && frozenClaimsCount > 0) {
      grounding.push(
        `${frozenClaimsCount} frozen claim${frozenClaimsCount === 1 ? '' : 's'}`,
      );
    }
    if (frozenDrawingsCount && frozenDrawingsCount > 0) {
      grounding.push(
        `${frozenDrawingsCount} frozen figure${frozenDrawingsCount === 1 ? '' : 's'}`,
      );
    }
    const summary = parts.join(' · ');
    return grounding.length
      ? `${summary} · grounded on ${grounding.join(' + ')}`
      : summary;
  })();

  return (
    <div
      className="flex items-center justify-between border-b border-zinc-200 bg-white px-6 py-3"
      data-testid="spec-header"
    >
      <div className="flex items-center gap-3 min-w-0">
        {onBack && (
          <button
            type="button"
            className={outlineBtn}
            onClick={onBack}
            data-testid="spec-back"
            aria-label="Back to matter workflow"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        )}
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-zinc-900">Specification</h1>
          {subtitle && (
            <p
              className="truncate text-xs text-zinc-500"
              data-testid="spec-header-subtitle"
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Phase 5 Slice 5E — dynamic auto-save indicator. */}
        <AutoSaveChip />

        {/* Phase 11 Slice 11C / BUG-036 — admin-only cost indicator.
            Gated at the mount point on the caller-supplied role so
            non-admins never even fetch /admin/spec/telemetry; the
            component itself also self-checks role for defence in
            depth. */}
        {isAdmin && <CostIndicator matterId={matterId} />}

        <button
          type="button"
          className={outlineBtn}
          disabled={geRunning || !hasEmptyDrafted}
          onClick={() => setGeDialogOpen(true)}
          data-testid="spec-generate-everything"
          title={
            geRunning
              ? "Generate everything is already running"
              : !hasEmptyDrafted
                ? "All drafted sections already have content"
                : "Generate drafts for every empty section"
          }
        >
          Generate everything
        </button>
        {/* Bulk reset — clears every unfrozen non-auto section's
            paragraphs in one DELETE so the drafter can re-run
            generate-everything from a clean slate. Only enabled
            when there's actually content to clear. Confirm prompt
            spells out the destructive scope before firing. */}
        <button
          type="button"
          className={outlineBtn}
          disabled={resettingAll || geRunning || !hasMinimumContent}
          onClick={handleResetAllClick}
          data-testid="spec-reset-all-sections"
          title={
            geRunning
              ? "Wait for generate-everything to finish before resetting"
              : !hasMinimumContent
                ? "Nothing to reset — no drafted paragraphs yet"
                : "Reset every unfrozen section to empty (frozen sections skipped)"
          }
        >
          {resettingAll ? 'Resetting all…' : 'Reset all'}
        </button>
        <GenerateEverythingHistory matterId={matterId} />
        {/* BUG-049 + FEAT-003 + FEAT-004 — unified export menu.
            Replaces the two plain buttons (Preview / Export)
            with split-button dropdowns:
              * Preview DOCX uses an authenticated blob fetch
                (auth cookies follow on cross-origin BACKEND_URL
                deploys) + loading state.
              * Export dropdown surfaces the matter's default
                jurisdiction + the other two as overrides
                (jurisdiction param goes to the existing export
                endpoint; matter.jurisdiction stays unchanged).
              * "Export with audit appendix" sets
                include_provenance=true on the same endpoint.
              * "Export audit report only" hits the new
                /export-audit-docx endpoint for a standalone
                Appendix A.1-A.10 DOCX.
              * Bundle ZIP keeps its existing modal. */}
        <SpecExportMenu
          matterId={matterId}
          hasMinimumContent={hasMinimumContent}
          matterJurisdiction={specDocument?.jurisdiction || null}
          onOpenBundleModal={() => setBundleModalOpen(true)}
        />

        {/* Opens in its own tab so the drafter can read the
            specification here while writing claim descriptions there —
            the two are worked on together. An anchor rather than
            window.open: browsers never popup-block a real link, and
            middle-click / cmd-click keep working. The href is the same
            hash the app writes for a deep link, so a copied URL lands
            on the same page. */}
        {onOpenDescription && (
          <a
            className={outlineBtn}
            href={descriptionHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="spec-open-description"
            aria-label="Description workbench (opens in a new tab)"
            title="Claim-by-claim Detailed Description workbench — opens in a new tab"
          >
            Description ↗
          </a>
        )}

        <button
          type="button"
          className={outlineBtn}
          onClick={() => setWordTargetsOpen(true)}
          data-testid="spec-open-word-targets"
          aria-label="Word targets"
          title="Set how long this specification should be, overall or by section"
        >
          Word targets
        </button>

        <CoursesLink onOpenCourses={onOpenCourses} testId="spec-courses-button" />

        {onOpenSettings && (
          <button
            type="button"
            className={outlineBtn}
            onClick={onOpenSettings}
            data-testid="spec-open-settings"
            aria-label="Spec module settings"
            title="Spec module settings"
          >
            <SettingsIcon className="h-4 w-4" />
            Settings
          </button>
        )}
      </div>
      {bundleModalOpen && (
        <BundleExportModal
          matterId={matterId}
          matterJurisdiction={specDocument?.jurisdiction ?? undefined}
          onClose={() => setBundleModalOpen(false)}
        />
      )}
      {geDialogOpen && (
        <GenerateEverythingDialog
          onClose={() => setGeDialogOpen(false)}
        />
      )}
      {wordTargetsOpen && (
        <WordTargetsDialog
          matterId={matterId}
          onClose={() => setWordTargetsOpen(false)}
        />
      )}
      {/* UX-F01 (Phase 27 3-F) — styled Reset-all confirm.
          Replaces the prior window.confirm() at this call site;
          dialog renders into a fixed-position layer above the
          spec header chrome. */}
      {confirmResetAllOpen && (
        <ConfirmDialog
          title="Reset every unfrozen section?"
          message={
            'All generated paragraphs across the matter will be '
            + 'permanently deleted. Frozen and auto-generated '
            + 'sections are skipped. This cannot be undone.'
          }
          confirmLabel="Reset all"
          isPending={resettingAll}
          onConfirm={() => { void handleResetAllConfirmed(); }}
          onCancel={() => setConfirmResetAllOpen(false)}
          testIdSuffix="spec-header-reset-all-confirm"
        />
      )}
    </div>
  );
}
